import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import findIndex from 'lodash/findIndex';
import { Form, Errors, actions } from 'react-redux-form';
import TrustedContactInfo from '../TrustedContactInfo';

import {
  fetchUser,
  setAccreditation,
} from '../../actions';
import {
  updateUser,
} from '../../actions/user';
import {
  addNotification,
} from '../../actions/notifications';
import AccountInputRow from '../Forms/Controls/AccountInputRow';
import {
  validateEmail,
  validatePhone,
  validateZipcode,
  validateNamePlain,
  validateAddress,
} from '../Forms/Validators';

import {
  baseManageAccountPath,
  enumAnnualIncome,
  enumTotalNetWorth,
  enumLiquidNetWorth,
  enumStates,
  enumCountries,
  PLACEHOLDER_OPT,
  initialManageAccountState,
  enumTrustedContactRelationship,
  folioAllowedSpecialChars,
} from '../Forms/Schemas/baseRrf';
import { isAccredited } from '../../helpers';
import './styles.css';
import { LAYOUT } from '../Layout/enums';


const options = {
  disabled: true,
};

const getPersonalFields = ({
  email,
  contact,
  address,
  change,
}) => (
  [{
    model: '.email',
    defaultValue: email,
    label: 'Email',
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
      isEmail: val => val && !validateEmail(val),
    },
    errorHandler: {
      messages: {
        required: 'An email is required.',
        isEmail: val => `${val} is not a valid Email Address.`,
      },
    },
  }, {
    model: '.phone.dayTelephone',
    defaultValue: contact.dayTelephone,
    parser: val => (val && val.replace(/\s|\(|\)|-/g, '')),
    mask: [' ', '(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    guide: false,
    maxLength: 15,
    label: 'Daytime Phone',
    inputType: 'text',
    onChange: ({ target: { value } }) => (
      change(`${baseManageAccountPath}.accountInfo.phone.dayTelephone`, value)
    ),
    errors: {
      required: val => !val || !val.length,
      isPhone: val => val && !validatePhone(val),
    },
    errorHandler: {
      messages: {
        required: 'A daytime phone number is required.',
        isPhone: val => `${val} is not a valid Telephone Number.`,
      },
    },
  }, {
    model: '.phone.eveningTelephone',
    defaultValue: contact.eveningTelephone,
    parser: val => (val && val.replace(/\s|\(|\)|-/g, '')),
    mask: [' ', '(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    guide: false,
    maxLength: 15,
    onChange: ({ target: { value } }) => {
      change(`${baseManageAccountPath}.accountInfo.phone.eveningTelephone`, value);
    },
    errors: {
      required: val => !val || !val.length,
      isPhone: val => val && !validatePhone(val),
    },
    errorHandler: {
      messages: {
        required: 'An evening phone number is required.',
        isPhone: val => `${val} is not a valid Telephone Number.`,
      },
    },
    label: 'Evening Phone',
    inputType: 'text',
  }, {
    model: '.address.line1',
    defaultValue: address.line1,
    label: 'Street Address Line 1',
    maxLength: 35,
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
      address: val => val && !validateAddress(val),
    },
    errorHandler: {
      messages: {
        required: 'A Street Address is required.',
        address: val => `The following characters are not allowed: ${
          val.replace(folioAllowedSpecialChars, '').split('')
            .filter((item, pos, self) => self.indexOf(item) === pos)
            .join('')}`,
      },
    },
  }, {
    model: '.address.line2',
    defaultValue: address.line2,
    label: 'Street Address Line 2 (Optional)',
    maxLength: 35,
    inputType: 'text',
    errors: {
      address: val => val && !validateAddress(val),
    },
    errorHandler: {
      messages: {
        address: val => `The following characters are not allowed: ${
          val.replace(folioAllowedSpecialChars, '').split('')
            .filter((item, pos, self) => self.indexOf(item) === pos)
            .join('')}`,
      },
    },
  }, {
    model: '.address.city',
    defaultValue: address.city,
    label: 'City',
    maxLength: 25,
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
    },
    errorHandler: {
      messages: {
        required: 'A City is required.',
      },
    },
  }, {
    model: '.address.state',
    defaultValue: address.state,
    display: enumStates[address.state],
    label: 'State',
    inputType: 'select',
    options: enumStates,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT,
    },
    errorHandler: {
      messages: {
        required: 'Please select a state.',
      },
    },
  }, {
    model: '.address.zipcode',
    defaultValue: address.zipcode,
    label: 'ZIP Code',
    maxLength: 5,
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
      isZipCode: val => val && !validateZipcode(val),
    },
    errorHandler: {
      messages: {
        required: 'A ZIP Code is required.',
        isZipCode: val => `${val} is not a valid ZIP Code.`,
      },
    },
  }, {
    model: '.address.country',
    defaultValue: address.country,
    display: enumCountries[address.country],
    label: 'Country',
    inputType: 'select',
    options: enumCountries,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT,
    },
    errorHandler: {
      messages: {
        required: 'Please select a country.',
      },
    },
  }]
);

const getTrustedContactFields = ({
  trustedContact,
  change,
  disabled,
}) => (
  [{
    model: '.trustedContact.firstName',
    defaultValue: trustedContact ? trustedContact.firstName : '',
    label: 'First Name',
    maxLength: 20,
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
      name: val => val && !validateNamePlain(val),
    },
    errorHandler: {
      messages: {
        required: 'First Name is required.',
        name: 'First Name must contain only letters',
      },
    },
  }, {
    model: '.trustedContact.lastName',
    defaultValue: trustedContact ? trustedContact.lastName : '',
    label: 'Last Name',
    maxLength: 35,
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
      name: val => val && !validateNamePlain(val),
    },
    errorHandler: {
      messages: {
        required: 'Last Name is required.',
        name: 'Last Name must contain only letters',
      },
    },
  }, {
    model: '.trustedContact.email',
    defaultValue: trustedContact ? trustedContact.email : '',
    label: 'Email',
    inputType: 'text',
    errors: {
      required: val => !val || !val.length,
      isEmail: val => val && !validateEmail(val),
    },
    errorHandler: {
      messages: {
        required: 'An email is required.',
        isEmail: val => `${val} is not a valid Email Address.`,
      },
    },
  }, {
    model: '.trustedContact.telephone',
    defaultValue: trustedContact ? trustedContact.telephone : '',
    parser: val => (val && val.replace(/\s|\(|\)|-/g, '')),
    mask: [' ', '(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    guide: false,
    maxLength: 15,
    label: 'Phone Number',
    inputType: 'text',
    onChange: ({ target: { value } }) => (
      change(`${baseManageAccountPath}.trustedContact.telephone`, value)
    ),
    errors: {
      required: val => !val || !val.length,
      isPhone: val => val && !validatePhone(val),
    },
    errorHandler: {
      messages: {
        required: 'A phone number is required.',
        isPhone: val => `${val} is not a valid Telephone Number.`,
      },
    },
  }, {
    model: '.trustedContact.relationship',
    defaultValue: trustedContact ? trustedContact.relationship : '',
    display: (() => {
      const foundIndex = trustedContact ? findIndex(
        enumTrustedContactRelationship,
        item => trustedContact.relationship === item[0],
      ) : -1;
      const showIndex = disabled ? 2 : 1;
      return trustedContact && foundIndex !== -1 && foundIndex !== 0 ? enumTrustedContactRelationship[foundIndex][1] : enumTrustedContactRelationship[0][showIndex];
    })(),
    label: 'Relationship',
    inputType: 'select',
    options: enumTrustedContactRelationship,
  }]
);

const getFinanceFields = ({ finances }) => (
  [{
    model: '.finances.annualIncome',
    defaultValue: finances.annualIncome,
    display: enumAnnualIncome[finances.annualIncome][1],
    label: 'Annual Income',
    hint: 'Total amount of income earned annually before taxes.',
    inputType: 'select',
    options: enumAnnualIncome,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT,
    },
    errorHandler: {
      messages: {
        required: 'Please select a annual income range.',
      },
    },
  }, {
    model: '.finances.totalNetWorth',
    defaultValue: finances.totalNetWorth,
    display: enumTotalNetWorth[finances.totalNetWorth][1],
    label: 'Total Net Worth',
    hint: 'Net worth is what you own minus what you owe excluding your primary residence.',
    inputType: 'select',
    options: enumTotalNetWorth,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT,
    },
    errorHandler: {
      messages: {
        required: 'Please select a total net worth range.',
      },
    },
  }, {
    model: '.finances.liquidNetWorth',
    defaultValue: finances.liquidNetWorth,
    display: enumLiquidNetWorth[finances.liquidNetWorth][1],
    label: 'Liquid Net Worth',
    hint: 'Liquid net worth is the part of your assets that can readily be turned into cash. This includes investments such as stocks and mutual funds but not real estate and cars.',
    inputType: 'select',
    options: enumLiquidNetWorth,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT,
    },
    errorHandler: {
      messages: {
        required: 'Please select a liquid net worth range.',
      },
    },
  }]
);


export class UnconnectedPersonalInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      disabled: true,
      ready: !!props.email,
      trustedContactShowForm: false,
      hasTrustedContactData: true,
      trustedContact: props.trustedContact,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.email || nextProps.email) {
      this.setState({ ready: true });
    }

    if (!isEqual(this.props.trustedContact, nextProps.trustedContact)) {
      this.setState({ trustedContact: nextProps.trustedContact });
    }
  }

  onSubmit = async () => {
    const { submitting } = this.state;
    if (submitting || !this.props.formData) {
      return;
    }
    const { annualIncome, totalNetWorth } = this.props.formData.finances;
    isAccredited(annualIncome, totalNetWorth, flag => this.props.setAccreditation(flag));
    const {
      email,
      address: primaryAddress,
      phone,
      finances,
    } = this.props.formData;
    const trustedContact = this.validateTrustedContact() ? this.props.formData.trustedContact : null;
    this.setState({ submitting: true });

    let contact;
    if (phone.dayTelephone || phone.eveningTelephone) {
      contact = {
        dayTelephone: phone.dayTelephone || undefined,
        eveningTelephone: phone.eveningTelephone || undefined,
      };
    }

    try {
      if (email !== this.props.email) {
        await this.props.updateUser({
          contact,
          email,
          identity: {
            primaryAddress,
          },
          finances,
          trustedContact,
        });
        this.props.addNotification(`Please check your ${email}
        inbox to verify your updated email address.`);
      } else {
        await this.props.updateUser({
          contact,
          identity: { primaryAddress },
          finances,
          trustedContact,
        });
      }
      await this.props.fetchUser({ cacheDuration: 0 });
      this.setState({
        disabled: true,
      });
    } catch (err) {
      const { error } = await err.res.json();
      switch (err.res.status) {
        case 400:
          if (error && error.validationErrors
              && Object.keys(error.validationErrors).length > 0) {
            Object.keys(error.validationErrors).forEach(key =>
              this.props.addNotification(`Invalid ${error.validationErrors[key].param}: ${error.validationErrors[key].msg}`));
          } else {
            this.props.addNotification('Save failed due to invalid data.  Please review your changes and try again.');
          }
          break;
        default:
          this.props.addNotification(`Save failed ${err.message}. Please try again later.`);
          break;
      }
    }
    this.setState({
      submitting: false,
    });
  };

  onTrustedContactDelete = () => {
    this.props.removeTrustedContact();
    this.setState({
      disabled: false,
      trustedContactShowForm: false,
      hasTrustedContactData: false,
      trustedContact: { ...initialManageAccountState.trustedContact },
    });
  };

  validateTrustedContact() {
    const {
      trustedContact,
    } = this.props.formData;
    return (
      trustedContact.firstName &&
      trustedContact.lastName &&
      trustedContact.email &&
      trustedContact.telephone
    );
  }

  toggleForm = (opts) => {
    const updatedState = { disabled: !this.state.disabled };
    if (opts && opts.type) {
      if (opts.type === 'cancel') {
        updatedState.trustedContactShowForm = false;
        updatedState.hasTrustedContactData = true;
        updatedState.trustedContact = { ...this.props.trustedContact };
      }
    }
    this.setState(updatedState);
  };

  addTrustedContact = async () => {
    await this.setState({
      hasTrustedContactData: true,
      trustedContactShowForm: true,
      disabled: false,
    });
  };

  render() {
    if (!this.state.ready) {
      return null;
    }

    const { disabled, submitting } = this.state;
    options.disabled = disabled;
    options.auto = 'labels';
    let inputMsg = 'Save';
    if (submitting) {
      inputMsg = 'Saving...';
    } else if (disabled) {
      inputMsg = 'Edit';
    }
    let styleSuff = '';
    switch (this.props.layout) {
      case LAYOUT.TAB:
        styleSuff = 'Tab';
        break;
      case LAYOUT.MOB:
        styleSuff = 'Mob';
        break;
      // no default
    }
    return (
      <div styleName={`formGroup${styleSuff}`}>
        <Form
          model={`${baseManageAccountPath}.accountInfo`}
          onSubmit={this.onSubmit}
          validators={{
            '': {
              netWorths: vals =>
                vals.finances && vals.finances.liquidNetWorth <= vals.finances.totalNetWorth,
            },
          }}
        >
          <div styleName="accountFormSection">
            <div styleName="personalInfoHeader">
              <div styleName={`labelBlack${styleSuff}`}>Personal Information</div>
              { disabled ? (
                <div
                  styleName="edit"
                  onClick={this.toggleForm}
                  onKeyPress={this.toggleForm}
                  role="button"
                  tabIndex={0}
                >
                  Edit
                </div>
                ) : (
                  <div styleName="saveOrCancel">
                    <input
                      id="btnEditAndSave"
                      type="submit"
                      value={inputMsg}
                      disabled={submitting}
                      styleName="formHeadlink"
                    />
                    { !submitting &&
                      <div
                        styleName="formHeadlink"
                        onClick={() => this.toggleForm({ type: 'cancel' })}
                        onKeyPress={() => this.toggleForm({ type: 'cancel' })}
                        role="link"
                        tabIndex={0}
                      >
                        Cancel
                      </div>
                    }
                  </div>
                )
              }
            </div>
            { getPersonalFields(this.props).map(f => (
              <AccountInputRow
                key={f.model}
                editMode={disabled ? 'static' : 'editting'}
                label={f.label}
                {...f}
              />
              )) }
          </div>
          <div styleName="accountFormSection">
            <div styleName="titleContainer">
              <div styleName={`labelBlack${styleSuff}`}>Trusted Contact Person (Optional)</div>
              {
                !this.state.disabled && ((this.state.hasTrustedContactData && this.state.trustedContactShowForm) || this.props.trustedContact) ?
                  <button styleName="trustedContactAddBtn" type="button" onClick={() => this.onTrustedContactDelete()}>Delete</button> : null
              }
            </div>
            <TrustedContactInfo />
            {
              (this.props.trustedContact || this.state.trustedContactShowForm) && this.state.hasTrustedContactData ?
                <div>
                  { getTrustedContactFields({ ...this.props, trustedContact: this.state.trustedContact, disabled: this.state.disabled }).map(f => (
                    <AccountInputRow
                      key={f.model}
                      editMode={disabled ? 'static' : 'editting'}
                      label={f.label}
                      {...f}
                    />
                  ))}
                </div> :
                <div styleName="emptyTrustedContactSection">
                  <div styleName="trustedContactNoDetails">You don&#39;t have trusted contact person details.</div>
                  <button styleName="trustedContactAddBtn" onClick={() => this.addTrustedContact()}>
                    Add Trusted Contact Person
                  </button>
                </div>
            }
          </div>
          <div styleName="accountFormSection">
            <div styleName={`labelBlack${styleSuff}`}>Assets</div>
            <div>
              { this.props.isAccredited ? <div styleName="additionalMessage">You are an accredited investor</div> : '' }
            </div>
            { getFinanceFields(this.props).map(f => (
              <AccountInputRow
                key={f.model}
                editMode={disabled ? 'static' : 'editting'}
                label={f.label}
                {...f}
              />
              )) }
            {!disabled && (
              <Errors
                styleName="error"
                show={form => form.touched || form.submitted}
                model={`${baseManageAccountPath}.accountInfo`}
                messages={{
                  netWorths: 'Liquid Net Worth can not be greater than Total Net Worth.',
                }}
              />
            )}
          </div>
        </Form>
      </div>
    );
  }
}

UnconnectedPersonalInfo.propTypes = {
  layout: PropTypes.number,
};

UnconnectedPersonalInfo.defaultProps = {
  layout: LAYOUT.DESK,
};

const mapStateToProps = state => ({
  authToken: state.authToken,
  address: get(state, 'user.data.identity.primaryAddress'),
  email: get(state, 'user.data.email'),
  finances: get(state, 'user.data.finances'),
  contact: get(state, 'user.data.contact'),
  formData: get(state, 'manageAccountForm.accountInfo'),
  trustedContact: get(state, 'user.data.trustedContact'),
  isAccredited: get(state, 'isAccredited'),
});

const mapDispatchToProps = dispatch => ({
  setAccreditation: (...args) => (dispatch(setAccreditation(...args))),
  updateUser: (...args) => (dispatch(updateUser(...args))),
  fetchUser: (...args) => (dispatch(fetchUser(...args))),
  addNotification: (...args) => (dispatch(addNotification(...args))),
  change: (model, value) => (dispatch(actions.change(model, value))),
  removeTrustedContact: async () => {
    await dispatch(actions.reset('manageAccountForm.accountInfo.trustedContact', {}));
    await dispatch(actions.reset('user.data.trustedContact', {}));
  },
});


export default connect(mapStateToProps, mapDispatchToProps)(UnconnectedPersonalInfo);



// WEBPACK FOOTER //
// ./src/components/ManageAccount/PersonalInfo.js